import BaseService from "@/services/BaseService.ts";
import {supabase} from "@supabaseClient";
import {Json} from "@supabaseTypes";
import {SaccoShare} from "@/@types/sacco.models.ts";
import {convertToAccountCurrency} from "@lib/currencyConversion/accountCurrency.ts";
import {convertLinkedSaccoAmount} from "./linkedAmount.ts";

/**
 * A service class for managing Sacco share transactions. This class extends the BaseService
 * and provides methods to facilitate the purchasing of Sacco shares with debit transactions.
 */
export class SaccoShareService extends BaseService {

    /**
     * Creates a Sacco share purchase transaction with debit.
     * This method uses a stored procedure to record the transaction.
     *
     * @param {string} userId - The unique identifier of the user initiating the transaction.
     * @param {SaccoShareWithDebitRequest} request - An object containing details of the share purchase and debit transaction.
     * @param {string} request.membershipId - The membership ID related to the transaction.
     * @param {string} request.sourceAccountId - The source account ID for the debit.
     * @param {number} request.units - The number of shares to be purchased.
     * @param {number} request.pricePerUnit - The price per unit of the share.
     * @param {string} request.shareCurrency - The currency in which the share price is denominated.
     * @param {number} [request.fee=0] - The transaction fee, defaulting to 0 if not provided.
     * @param {string} request.purchaseDate - The date of the share purchase.
     * @param {string} [request.note=null] - An optional note or comment for the transaction.
     * @return {Promise<Json>} A promise resolving to a JSON object containing the result of the operation.
     * @throws {Error} Throws an error if the transaction fails or if no data is returned.
     */
    public async createShareWithDebit(userId: string, request: SaccoShareWithDebitRequest): Promise<Json> {
        try {
            const fee = request.fee ?? 0;
            const { convertedAmount, convertedFee } = await convertToAccountCurrency(
                userId, request.sourceAccountId, request.units * request.pricePerUnit, fee, request.shareCurrency
            );
            const { data, error } = await supabase.rpc('create_sacco_share_with_debit', {
                p_user_id: userId,
                p_membership_id: request.membershipId,
                p_source_account_id: request.sourceAccountId,
                p_units: request.units,
                p_price_per_unit: request.pricePerUnit,
                p_share_currency: request.shareCurrency,
                p_fee: fee,
                p_purchase_date: request.purchaseDate,
                p_note: request.note ?? null,
                p_debit_amount: convertedAmount,
                p_debit_fee: convertedFee,
            });

            if (error) throw new Error(error.message || 'Failed to record share purchase');
            if (!data) throw new Error('No data returned from share purchase creation');
            return data;
        } catch (error) {
            console.error("Error creating Sacco deposit with debit", error);
            await this.handleError(error);
        }
    }

    /**
     * Creates a new sacco share for the user
     *
     * @deprecated use createShareWithDebit instead
     *
     * @param {string} userId User's ID
     * @param {string} membershipId membership ID
     * @param {SaccoShareCreateRequest} request request to create a sacco share
     */
    async create(userId: string, membershipId: string, request: SaccoShareCreateRequest): Promise<Json> {
        const payload = this.convertRequestToSnakeCase(request);
        try {
            const { data, error } = await supabase
                .from('sacco_shares')
                .insert({ ...payload, membership_id: membershipId, user_id: userId })
                .select()
                .single();
            if (error) throw error;
            return data
        } catch (error) {
            console.error("Error creating Sacco deposit", error);
            await this.handleError(error);
        }
    }

    /**
     * Fetch Sacco shares by membership
     * @param {string} userId User Id
     * @param {string} membershipId Membership Id
     */
    async fetchByMembership(userId: string, membershipId: string): Promise<SaccoShare[]> {
        try {
            const { data, error } = await supabase
                .from('sacco_shares')
                .select('*')
                .eq('membership_id', membershipId)
                .eq('user_id', userId)
                .order('purchase_date', { ascending: true });
            if (error) throw error;
            return data
        } catch (error) {
            console.error(`Error fetching Sacco shares by membership: ${membershipId}`, error);
            await this.handleError(error);
        }
    }

    /**
     * Updates a sacco share. A share bought with a debit has its linked transaction re-priced and the
     * source account balance adjusted in the same database transaction.
     * @param {string} userId User's ID
     * @param {SaccoShareUpdateRequest} request request to update a sacco share
     */
    async update(userId: string, request: SaccoShareUpdateRequest): Promise<SaccoShare> {
        try {
            const debitAmount = await convertLinkedSaccoAmount(
                userId, 'sacco_shares', request.id, request.units * request.pricePerUnit
            );
            const { data, error } = await supabase.rpc('update_sacco_share_with_debit', {
                p_share_id: request.id,
                p_user_id: userId,
                p_units: request.units,
                p_price_per_unit: request.pricePerUnit,
                p_purchase_date: request.purchaseDate,
                p_note: request.note ?? null,
                p_debit_amount: debitAmount,
            });
            if (error) throw error;
            return data
        } catch (error) {
            console.error(`Error updating Sacco share: ${request.id}`, error);
            await this.handleError(error);
        }
    }

    /**
     * Deletes a sacco share for a given user. A share bought with a debit is deleted together with
     * its transaction, and the source account's balance is restored.
     * @param {string} id Sacco share ID
     * @param {string} userId User's ID
     */
    async delete(id: string, userId: string): Promise<void> {
        try {
            const { data: share, error: fetchError } = await supabase
                .from('sacco_shares')
                .select('transaction_id')
                .eq('id', id)
                .single();
            if (fetchError) throw fetchError;

            if (share?.transaction_id) {
                const { error } = await supabase.rpc('delete_transaction_with_balance', {
                    p_transaction_id: share.transaction_id,
                    p_user_id: userId,
                });
                if (error) throw error;
                return;
            }

            const { error } = await supabase.from('sacco_shares').delete().eq('id', id);
            if (error) throw error;
        } catch (error) {
            console.error(`Error deleting sacco share: ${id}`, error);
            await this.handleError(error);
        }
    }
}

export default new SaccoShareService();
